import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Circle, Edit3, Heart, MapPin, Pin, RotateCcw, Trash2 } from 'lucide-react';
import type { Category, PlannerEvent, PlannerRecord, RecordKind, ShoppingItem, Task, Note } from '../../types';

type Props = {
  category?: Category;
  completedOverride?: boolean;
  kind: RecordKind;
  onDelete: () => void;
  onEdit: () => void;
  onToggleChecklist: (itemId: string) => void;
  onToggleComplete: () => void;
  onToggleFavorite: () => void;
  onTogglePinned: () => void;
  record: PlannerRecord;
  timeFormat: 12 | 24;
};

const getTitle = (record: PlannerRecord) => ('name' in record ? record.name : record.title) || 'Untitled note';

const formatTime = (value: string, timeFormat: 12 | 24) => {
  if (timeFormat === 24) return value;
  const [hours, minutes] = value.split(':').map(Number);
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
};

export const RecordCard = ({
  category,
  completedOverride,
  kind,
  onDelete,
  onEdit,
  onToggleChecklist,
  onToggleComplete,
  onToggleFavorite,
  onTogglePinned,
  record,
  timeFormat
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const task = kind === 'task' ? record as Task : null;
  const event = kind === 'event' ? record as PlannerEvent : null;
  const note = kind === 'note' ? record as Note : null;
  const shopping = kind === 'shopping' ? record as ShoppingItem : null;
  const completed = completedOverride ?? (task?.status === 'completed' || shopping?.completed === true);
  const body = task?.description || event?.description || note?.content || shopping?.notes || '';
  const checklist = task?.checklist ?? event?.checklist ?? note?.checklist ?? [];
  const hasDetails = Boolean(body || checklist.length > 0);

  return (
    <article className="record-card" data-completed={completed} data-expanded={expanded}>
      <div className="record-row">
        <div className="record-main">
          {(task || shopping) && (
            <button
              type="button"
              className="completion-button"
              aria-label={completed ? `Reopen ${getTitle(record)}` : `Complete ${getTitle(record)}`}
              onClick={onToggleComplete}
            >
              {completed ? <Check size={16} /> : <Circle size={16} />}
            </button>
          )}

          <div className="record-identity">
            <h3 className="record-title" title={getTitle(record)}>{getTitle(record)}</h3>

            <div className="record-chips">
              {category && (
                <span className="category-chip">
                  <i style={{ backgroundColor: category.color }} />
                  {category.name}
                </span>
              )}
              {(task || shopping) && (
                <span className={`priority-chip priority-${task?.priority ?? shopping?.priority}`}>
                  {task?.priority ?? shopping?.priority}
                </span>
              )}
              {task && <span className="record-chip">{task.status}</span>}
              {((task && task.recurrence !== 'none') || (event && event.recurrence !== 'none')) && (
                <span className="record-chip">
                  <RotateCcw size={11} />
                  {task?.recurrence ?? event?.recurrence}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="record-meta-inline">
          {task?.dueDate && (
            <span>{task.dueDate}{task.dueTime ? ` at ${formatTime(task.dueTime, timeFormat)}` : ''}</span>
          )}
          {event && (
            <span>{event.date}{event.startTime ? ` at ${formatTime(event.startTime, timeFormat)}` : ''}</span>
          )}
          {event?.location && (
            <span title={event.location}><MapPin size={12} />{event.location}</span>
          )}
          {shopping?.quantity && <span>Qty: {shopping.quantity}</span>}
          {shopping?.store && <span title={shopping.store}>{shopping.store}</span>}
          {(task?.reminder || event?.reminder) && (
            <span>Rem: {formatTime((task?.reminder ?? event?.reminder)!, timeFormat)}</span>
          )}
        </div>

        <div className="record-actions">
          {hasDetails && (
            <button
              type="button"
              className="icon-button small-icon-button"
              aria-label={expanded ? 'Collapse details' : 'Expand details'}
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
          )}
          {note && (
            <button
              type="button"
              className="icon-button small-icon-button"
              aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
              onClick={onTogglePinned}
            >
              <Pin size={15} fill={note.pinned ? 'currentColor' : 'none'} />
            </button>
          )}
          <button
            type="button"
            className="icon-button small-icon-button"
            aria-label={record.favorite ? 'Remove from favorites' : 'Add to favorites'}
            onClick={onToggleFavorite}
          >
            <Heart size={15} fill={record.favorite ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            className="icon-button small-icon-button"
            aria-label={`Edit ${getTitle(record)}`}
            onClick={onEdit}
          >
            <Edit3 size={15} />
          </button>
          <button
            type="button"
            className="icon-button small-icon-button destructive-button"
            aria-label={`Delete ${getTitle(record)}`}
            onClick={onDelete}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {expanded && body && <p className="record-body">{body}</p>}
      {expanded && checklist.length > 0 && (
        <ul className="checklist">
          {checklist.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                aria-label={`${item.completed ? 'Reopen' : 'Complete'} ${item.text}`}
                onClick={() => onToggleChecklist(item.id)}
              >
                {item.completed ? <Check size={14} /> : <Circle size={14} />}
                <span>{item.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};
